// CustomerBooking.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import ApiServices from "../../Services/ApiServices/ApiServices";

const BRAND_COLOR = "#094c9f";

const CustomerBooking = () => {
  // customer fields (prefilled from sessionStorage 'user')
  const [customer, setCustomer] = useState({
    id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    zip_code: "",
  });

  // services
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // form state
  const [selectedService, setSelectedService] = useState("");
  const [selectedDate, setSelectedDate] = useState(""); // yyyy-mm-dd
  const [submitting, setSubmitting] = useState(false);

  // ui
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // load user from sessionStorage
  useEffect(() => {
    let raw = null;
    try {
      raw = sessionStorage.getItem("user");
    } catch {
      raw = null;
    }
    if (raw) {
      try {
        const u = JSON.parse(raw);
        setCustomer({
          id: u.id ?? "",
          first_name: u.first_name ?? "",
          last_name: u.last_name ?? "",
          email: u.email ?? "",
          phone: u.phone ?? "",
          address: u.address ?? "",
          zip_code: u.zip_code ?? "",
        });
      } catch (e) {
        console.error("Failed to parse user from sessionStorage", e);
      }
    }
  }, []);

  // fetch services
  useEffect(() => {
    const fetch = async () => {
      setLoadingServices(true);
      try {
        const res = await ApiServices.get("service-offerings/");
        const data = res?.data ?? res;
        setServices(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setSnack({ open: true, message: "Failed to load services", severity: "error" });
      } finally {
        setLoadingServices(false);
      }
    };
    fetch();
  }, []);

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // helper: get tomorrow's date in yyyy-mm-dd for min attr
  const tomorrowYMD = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // build ISO timestamp from selectedDate and default time 09:30 local
  const buildScheduledAtISO = (dateYmd) => {
    if (!dateYmd) return null;
    // dateYmd like "2025-11-28"
    // use default local time 09:30:00
    const [y, m, d] = dateYmd.split("-");
    const localDate = new Date(Number(y), Number(m) - 1, Number(d), 9, 30, 0, 0); // local time
    return localDate.toISOString(); // converts to UTC ISO with Z
  };

  const validateForm = () => {
    if (!customer.id) {
      setSnack({ open: true, message: "Customer id missing (not logged in?)", severity: "error" });
      return false;
    }
    if (!selectedService) {
      setSnack({ open: true, message: "Please select a service", severity: "error" });
      return false;
    }
    if (!selectedDate) {
      setSnack({ open: true, message: "Please select a future date", severity: "error" });
      return false;
    }
    // ensure date is > today
    const chosen = new Date(selectedDate + "T00:00:00");
    const today = new Date();
    // set today's time to 00:00 to compare days
    today.setHours(0, 0, 0, 0);
    if (chosen <= today) {
      setSnack({ open: true, message: "Please choose a future date (tomorrow or later)", severity: "error" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (!validateForm()) return;

    const scheduled_at = buildScheduledAtISO(selectedDate);
    const payload = {
      customer: Number(customer.id),
      scheduled_at,
      address: customer.address || "",
      zip_code: customer.zip_code || "",
      services: [Number(selectedService)],
    };

    setSubmitting(true);
    try {
      await ApiServices.post("service-appointments/", payload);
      setSnack({ open: true, message: "Appointment created successfully", severity: "success" });
      // clear selected service and date, reset time choice
      setSelectedService("");
      setSelectedDate("");
      // optionally reset customer fields to session user again (no overwrite if user edited)
      let raw = null;
      try {
        raw = sessionStorage.getItem("user");
      } catch {
        raw = null;
      }
      if (raw) {
        try {
          const u = JSON.parse(raw);
          setCustomer({
            id: u.id ?? "",
            first_name: u.first_name ?? "",
            last_name: u.last_name ?? "",
            email: u.email ?? "",
            phone: u.phone ?? "",
            address: u.address ?? "",
            zip_code: u.zip_code ?? "",
          });
        } catch {}
      }
    } catch (err) {
      console.error(err);
      let msg = "Failed to create appointment.";
      if (err?.response?.data) {
        const d = err.response.data;
        if (typeof d === "string") msg = d;
        else if (d?.detail) msg = d.detail;
        else msg = Object.values(d).flat().join(" ") || msg;
      } else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%", boxSizing: "border-box" }}>
      <Paper elevation={2} sx={{ p: 3, maxWidth: 820, margin: "0 auto" }}>
        <Typography variant="h5" sx={{ color: BRAND_COLOR, fontWeight: 700, mb: 2 }}>
          Book an Appointment
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Customer info (editable) */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <TextField
              label="First name"
              value={customer.first_name}
              onChange={(e) => setCustomer((c) => ({ ...c, first_name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Last name"
              value={customer.last_name}
              onChange={(e) => setCustomer((c) => ({ ...c, last_name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Email"
              value={customer.email}
              onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Phone"
              value={customer.phone}
              onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
              required
              fullWidth
            />
          </Box>

          <TextField
            label="Address"
            value={customer.address}
            onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))}
            multiline
            rows={2}
            fullWidth
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Zip code"
              value={customer.zip_code}
              onChange={(e) => setCustomer((c) => ({ ...c, zip_code: e.target.value }))}
              sx={{ width: 200 }}
            />

            <FormControl fullWidth>
              <InputLabel id="service-select-label">Service</InputLabel>
              <Select
                labelId="service-select-label"
                value={selectedService}
                label="Service"
                onChange={(e) => setSelectedService(e.target.value)}
                required
              >
                {loadingServices ? (
                  <MenuItem value="">
                    <em>Loading services...</em>
                  </MenuItem>
                ) : services.length === 0 ? (
                  <MenuItem value="">
                    <em>No services available</em>
                  </MenuItem>
                ) : (
                  services.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name} — ₹{typeof s.price === "string" ? parseFloat(s.price).toFixed(2) : s.price}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Select date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: tomorrowYMD() }}
            />
            <Typography variant="body2" color="text.secondary">
              Time will be scheduled automatically at 09:30 local time.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 1 }}>
            <Button
              onClick={() => {
                // reset to session user values and clear selection
                let raw = null;
                try {
                  raw = sessionStorage.getItem("user");
                } catch {
                  raw = null;
                }
                if (raw) {
                  try {
                    const u = JSON.parse(raw);
                    setCustomer({
                      id: u.id ?? "",
                      first_name: u.first_name ?? "",
                      last_name: u.last_name ?? "",
                      email: u.email ?? "",
                      phone: u.phone ?? "",
                      address: u.address ?? "",
                      zip_code: u.zip_code ?? "",
                    });
                  } catch {}
                }
                setSelectedService("");
                setSelectedDate("");
              }}
            >
              Reset
            </Button>

            <Button
              type="submit"
              variant="contained"
              sx={{ backgroundColor: BRAND_COLOR }}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={20} /> : "Book Appointment"}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={4500} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerBooking;
